import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Selectors } from "../../Everou/_reducers/app.reducer";
import { SimpleToggle } from "../../Everou/_components";
import { CapsuleButton } from "../../Common/_components";
import { CustomNumberInput } from "../../Common/_components/CustomNumberInput";
import { ListedGroup } from "../../Everou/_components/ListedGroup";
import { DeviceHelper } from "../../Everou/_helpers";
import { PMSLocationsActions } from "../_actions/PMSLocationsActions";
import { VACATION_RENTAL_PLANS } from "../_constants/vacationRentalPlan.constants";
import ToolTipWrapper from "../../Everou/_components/ToolTipWrapper";
import { Icons } from "../../Everou/_assets";
import { PMSLocationProtectLogs } from "./PMSLocationProtectLogs";
import {
    SettingInheritanceStatus,
    SettingsSection,
} from "./_components/PMSLocationSettingsFields";
import { CustomModal } from "../../Everou/_components/CustomModal";
import { ProtectHelper } from "../../Everou/_helpers/ProtectHelper";
import { IncorrectDevices } from "../../Everou/ProtectModule/IncorrectDevices";
import { deviceTypeName } from "../../Everou/_constants";

// Door-open alert: backend stores a threshold in seconds (0 = off). The UI
// works in minutes (seconds / 60) and defaults to 15 min when turned on.
const SECURITY_ALERT_DEFAULT_MINUTES = 15;
const SECURITY_ALERT_MAX_MINUTES = 546; // 546 * 60 = 32760 <= 32767 (backend max)

export function PMSLocationAlarm() {

    // LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // GLOBAL STATE
    const locationInfo = useSelector(Selectors.getPMSLocationInfo);
    const devices = useSelector(Selectors.getPMSLocationDevices);
    // When the device never reported a status, `status` arrives null and
    // localStatus can't be resolved. The shared label code defaults null→0, so
    // it would misleadingly read e.g. "Cerrado con cerrojo"; show "unknown"
    // instead (the protect logic already flags it as not-confirmed-safe).
    const incorrectDevices = ProtectHelper.setIncorrectDevices(devices).map(device =>
        device.localStatus == null
            ? { ...device, statusLabel: t("mod_pms_location_alarm_device_status_unknown") }
            : device
    );

    // LOCAL STATE — mirrors the server value so the toggle reflects the
    // automatic backend changes (the alarm arms/disarms by itself with the
    // auto mode) once a re-fetch lands.
    const [protectLocal, setProtectLocal] = useState(locationInfo.isProtectOn);
    const [autoProtectLocal, setAutoProtectLocal] = useState(locationInfo.protectAutoEnabledWithoutReservations);
    const [openPopup, setOpenPopup] = useState(null); // null | "devices" | "activity"
    const [triggeredCount, setTriggeredCount] = useState(0);
    const [confirmEnable, setConfirmEnable] = useState(false);
    const [protectGuestLocal, setProtectGuestLocal] = useState(locationInfo.enableProtectGuest);
    const [securityAlertEnabledLocal, setSecurityAlertEnabledLocal] = useState(
        locationInfo.securityAlertTime > 0,
    );
    const [securityAlertMinutesLocal, setSecurityAlertMinutesLocal] = useState(
        locationInfo.securityAlertTime > 0
            ? Math.round(locationInfo.securityAlertTime / 60)
            : SECURITY_ALERT_DEFAULT_MINUTES,
    );
    // Debounce the backend write so typing/stepping the minutes field coalesces
    // into a single LOCATION_UPDATE instead of one per keystroke. The pending
    // payload travels with the timer (seconds AND the location it belongs to)
    // so leaving the screen can flush it without posting to whichever location
    // is open by then.
    const securityAlertSaveTimer = useRef(null);
    const securityAlertPendingSave = useRef(null);

    function inheritedControl(field, serverField, control) {
        return (
            <div>
                {control}
                <SettingInheritanceStatus
                    ownSettings={locationInfo.ownSettings}
                    field={field}
                    organizationName={locationInfo.settingsOrganizationName}
                    onReset={() => dispatch(
                        PMSLocationsActions.clearOrganizationSettingOverride(
                            serverField,
                            locationInfo.uid,
                        ),
                    )}
                />
            </div>
        );
    }

    useEffect(() => {
        setProtectLocal(locationInfo.isProtectOn);
    }, [locationInfo.isProtectOn]);

    useEffect(() => {
        setProtectGuestLocal(locationInfo.enableProtectGuest);
    }, [locationInfo.enableProtectGuest]);

    useEffect(() => {
        const seconds = locationInfo.securityAlertTime;
        setSecurityAlertEnabledLocal(seconds > 0);
        // Off is stored as 0, which carries no threshold: keep showing the
        // minutes the field already had, or turning the alert off and on again
        // would silently reset a configured 40 min back to the default.
        if (seconds > 0)
            setSecurityAlertMinutesLocal(Math.round(seconds / 60));
    }, [locationInfo.securityAlertTime]);

    // The debounced write is in flight for up to 600 ms, and switching tab
    // unmounts this screen well inside that window: flush it instead of
    // dropping it, or a change made right before leaving is lost in silence.
    useEffect(() => () => {
        if (!securityAlertSaveTimer.current)
            return;

        clearTimeout(securityAlertSaveTimer.current);
        const pending = securityAlertPendingSave.current;
        if (pending)
            dispatch(PMSLocationsActions.updateSecurityAlertTime(pending.seconds, pending.uid));
    }, [dispatch]);

    useEffect(() => {
        setAutoProtectLocal(locationInfo.protectAutoEnabledWithoutReservations);
    }, [locationInfo.protectAutoEnabledWithoutReservations]);

    // Whether the alarm has gone off: non-empty grouped trigger logs. Only
    // meaningful while armed — with the alarm disarmed the backend ignores the
    // protect filter and returns regular activity logs, so skip the fetch and
    // clear the banner (same implicit guard as the Everou Protect module).
    useEffect(() => {
        if (!locationInfo.isProtectOn) {
            setTriggeredCount(0);
            return;
        }
        let cancelled = false;
        (async () => {
            const logs = await dispatch(PMSLocationsActions.getTriggeredProtectLogs());
            if (!cancelled) setTriggeredCount((logs || []).length);
        })();
        return () => { cancelled = true; };
    }, [dispatch, locationInfo.uid, locationInfo.isProtectOn]);

    // VIEW
    return (
        <div className="h-100 d-flex no-gutters flex-column">
            {triggeredBanner()}
            {incorrectDevicesBadge()}
            {/* Automatic protection first: it is how the alarm is normally
                driven, and the manual arm/disarm below is the exception. */}
            {locationInfo.vacationRentalPlan === VACATION_RENTAL_PLANS.PREMIUM_PLAN &&
                autoProtectSection()
            }
            {protectSection()}
            {locationInfo.vacationRentalPlan === VACATION_RENTAL_PLANS.PREMIUM_PLAN &&
                <>
                    {protectGuestSection()}
                    {securityAlertSection()}
                </>
            }
            {devicesAndActivityButtons()}
            {alarmPopup()}
            {confirmEnableModal()}
        </div>
    );

    function enableProtect(value) {
        setProtectLocal(value);
        dispatch(PMSLocationsActions.changeProtectStatus(value, locationInfo.uid));
    }

    function confirmEnableModal() {
        if (!confirmEnable) return null;

        return (
            <CustomModal
                show={true}
                centered
                onHide={() => setConfirmEnable(false)}
                title={t("protect.enableProtectQuestion")}
                content={<div>{t("protect.devicesIsIncorrectState")}</div>}
                footer={
                    <>
                        <CapsuleButton
                            text={t("common.cancel")}
                            style="INFO"
                            onClick={() => setConfirmEnable(false)}
                        />
                        <CapsuleButton
                            text={t("mod_pms_location_protect_arm")}
                            style="PRIMARY"
                            onClick={() => {
                                setConfirmEnable(false);
                                enableProtect(true);
                            }}
                        />
                    </>
                }
            />
        );
    }

    function triggeredBanner() {
        if (triggeredCount <= 0) return null;

        return (
            <div className="alert alert-danger font-weight-bold mb-3" role="alert">
                {t("mod_pms_location_alarm_triggered_banner")}
            </div>
        );
    }

    function incorrectDevicesBadge() {
        if (incorrectDevices.length === 0) return null;

        return (
            <div className="mb-3">
                <span
                    className="text-danger font-weight-bold cp d-inline-flex align-items-center"
                    style={{ textDecoration: "underline" }}
                    onClick={() => setOpenPopup("devices")}
                >
                    ⚠ {t("mod_pms_location_alarm_incorrect_devices", { count: incorrectDevices.length })}
                    <span className="ml-1" style={{ textDecoration: "none" }}>›</span>
                </span>
            </div>
        );
    }

    function protectSection() {

        // Gated by the protect subscription (which already accounts for
        // Premium), not the raw plan — a Premium location can arm/disarm the
        // alarm manually without a subscription error.
        if (!locationInfo.hasProtectSubscription) return null;

        //ACTION HANDLERS
        function handleToggleProtect(value) {
            // Enabling with devices in an incorrect state → confirm first, like
            // the normal Protect (GLOBAL_POPUPS.ENABLE_PROTECT).
            if (value && incorrectDevices.length > 0) {
                setConfirmEnable(true);
                return;
            }
            enableProtect(value);
        }

        return (
            <SettingsSection
                title={t("mod_pms_location_protect_section_title")}
                tooltip={t("mod_pms_location_protect_section_tooltip")}
            >
                <div className="mt-2 mb-2 d-flex align-items-center">
                    <span className="mr-2">{t("mod_pms_location_auto_protect_status_label")}</span>
                    <span
                        className={`badge ${protectLocal ? "badge-success" : "badge-secondary"}`}
                        style={{ fontSize: "0.9rem" }}
                    >
                        {protectLocal
                            ? t("mod_pms_location_auto_protect_status_armed")
                            : t("mod_pms_location_auto_protect_status_disarmed")
                        }
                    </span>
                </div>
                <div className="mb-2 d-flex">
                    <CapsuleButton
                        text={protectLocal
                            ? t("mod_pms_location_protect_disarm")
                            : t("mod_pms_location_protect_arm")
                        }
                        style={protectLocal
                            ? "DANGER"
                            : "PRIMARY"
                        }
                        onClick={() => handleToggleProtect(!protectLocal)}
                    />
                </div>
            </SettingsSection>
        );
    }

    function autoProtectSection() {

        const isBusinessPlan =
            locationInfo.vacationRentalPlan === VACATION_RENTAL_PLANS.PREMIUM_PLAN;

        if (!isBusinessPlan) return null;

        //ACTION HANDLERS
        function handleChangeAutoProtect(value) {
            setAutoProtectLocal(value);
            dispatch(PMSLocationsActions.changeAutoProtectSetting(value, locationInfo.uid));
        }

        return (
            <SettingsSection
                title={t("mod_pms_location_auto_protect_section_title")}
                tooltip={t("mod_pms_location_auto_protect_section_tooltip")}
            >
                <div className="text-secondary mt-1 mb-2">
                    {t("mod_pms_location_auto_protect_explanation")}
                </div>
                <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
                    <div className="position-relative">
                        {t("mod_pms_location_auto_protect_enabled_title")}
                    </div>
                    <div className="ml-2" />
                    {inheritedControl("protectAutoEnabledWithoutReservations", "protect_auto_enabled_without_reservations", <SimpleToggle
                        className="c-toggle-smooth"
                        checked={autoProtectLocal}
                        handleChange={({ detail: { value } }) => handleChangeAutoProtect(value)}
                    />)}
                </div>
                {autoProtectLocal && <>
                    <div className="mt-2 mb-1 d-flex align-items-center justify-content-between">
                        <div className="position-relative">
                            {t("mod_pms_location_auto_protect_status_label")}
                        </div>
                        <div className={`font-weight-bold ${locationInfo.isProtectOn ? "text-success" : "text-secondary"}`}>
                            {locationInfo.isProtectOn
                                ? t("mod_pms_location_auto_protect_status_armed")
                                : t("mod_pms_location_auto_protect_status_disarmed")
                            }
                        </div>
                    </div>
                    <div className="text-secondary mb-2">
                        {t("mod_pms_location_auto_protect_managed_note")}
                    </div>
                </>}
            </SettingsSection>
        );
    }

    // Business-plan feature: gated by the caller, same as the sections around
    // it. The switch surfaces an alarm button in the guest's link, but what it
    // decides is who may arm the alarm, so it belongs with the other two modes.
    function protectGuestSection() {

        //ACTION HANDLERS
        function handleChangeProtectGuest(value) {
            setProtectGuestLocal(value);
            dispatch(PMSLocationsActions.updateEnableProtectGuest(value, locationInfo.uid));
        }

        return (
            <SettingsSection
                title={t("mod_pms_location_protect_guest_section_title")}
                tooltip={t("mod_pms_location_protect_guest_section_tooltip")}
            >
                <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
                    <div className="position-relative">
                        {t("mod_pms_location_protect_guest_enabled_title")}
                    </div>
                    <div className="ml-2" />
                    {inheritedControl("enableProtectGuest", "enable_protect_guest", <SimpleToggle
                        className="c-toggle-smooth"
                        checked={protectGuestLocal}
                        handleChange={({ detail: { value } }) => handleChangeProtectGuest(value)}
                    />)}
                </div>
            </SettingsSection>
        );
    }

    function securityAlertSection() {

        // Business-plan feature: gated by the caller, same as the automatic
        // protection above.

        //ACTION HANDLERS
        function saveSecurityAlertSeconds(seconds) {
            if (securityAlertSaveTimer.current)
                clearTimeout(securityAlertSaveTimer.current);

            securityAlertPendingSave.current = { seconds, uid: locationInfo.uid };
            securityAlertSaveTimer.current = setTimeout(() => {
                securityAlertPendingSave.current = null;
                dispatch(PMSLocationsActions.updateSecurityAlertTime(seconds, locationInfo.uid));
            }, 600);
        }

        function clampMinutes(minutes) {
            if (!minutes || isNaN(minutes))
                return 1;

            return Math.min(Math.max(Math.round(minutes), 1), SECURITY_ALERT_MAX_MINUTES);
        }

        function handleToggleSecurityAlert(value) {
            setSecurityAlertEnabledLocal(value);
            const seconds = value ? clampMinutes(securityAlertMinutesLocal) * 60 : 0;
            saveSecurityAlertSeconds(seconds);
        }

        function handleChangeSecurityAlertMinutes(minutes) {
            setSecurityAlertMinutesLocal(minutes);
            saveSecurityAlertSeconds(clampMinutes(minutes) * 60);
        }

        // Normalize a cleared/invalid field back to a valid value on blur so the
        // local state can't go stale while showing blank.
        function handleBlurSecurityAlertMinutes(minutes) {
            const clamped = clampMinutes(minutes);
            setSecurityAlertMinutesLocal(clamped);
            saveSecurityAlertSeconds(clamped * 60);
        }

        return (
            <SettingsSection
                title={t("mod_pms_location_security_alert_section_title")}
                tooltip={t("mod_pms_location_security_alert_section_tooltip")}
            >
                <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
                    <div className="position-relative">
                        {t("mod_pms_location_security_alert_enabled_title")}
                    </div>
                    <div className="ml-2" />
                    {inheritedControl("securityAlertTime", "security_alert_time", <SimpleToggle
                        className="c-toggle-smooth"
                        checked={securityAlertEnabledLocal}
                        handleChange={({ detail: { value } }) => handleToggleSecurityAlert(value)}
                    />)}
                </div>
                {securityAlertEnabledLocal &&
                    <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
                        <div className="position-relative">
                            {t("mod_pms_location_security_alert_minutes_title")}
                        </div>
                        <CustomNumberInput
                            value={securityAlertMinutesLocal}
                            min={1}
                            max={SECURITY_ALERT_MAX_MINUTES}
                            onChange={handleChangeSecurityAlertMinutes}
                            onBlur={handleBlurSecurityAlertMinutes}
                        />
                    </div>
                }
            </SettingsSection>
        );
    }

    function devicesAndActivityButtons() {
        return (
            <div className="d-flex flex-column align-items-start">
                <div className="mb-2 d-flex align-items-center">
                    <CapsuleButton
                        text={t("mod_pms_location_alarm_view_devices")}
                        onClick={() => setOpenPopup("devices")}
                    />
                    {infoTooltip(t("mod_pms_location_alarm_devices_tooltip"))}
                </div>
                <div className="d-flex align-items-center">
                    <CapsuleButton
                        text={t("mod_pms_location_alarm_view_activity")}
                        onClick={() => setOpenPopup("activity")}
                    />
                    {infoTooltip(t("mod_pms_location_alarm_activity_tooltip"))}
                </div>
            </div>
        );
    }

    function selectableDevicesList(buttonType, list) {
        const headerText = buttonType === "ADD"
            ? t("common.notIncluded")
            : t("common.included");

        return (
            <ListedGroup
                isInnerScrollEnabled={true}
                headerText={headerText}
                elements={list.map(device => singleDevice(device, buttonType))}
            />
        );
    }

    function singleDevice(device, buttonType) {
        let uiButtonType = "c-btn-primary";
        let actionButtonName = "+";

        if (buttonType === "REMOVE") {
            uiButtonType = "btn-danger";
            actionButtonName = "-";
        }

        return (
            <div key={device.uid} className="
                flex-shrink-0
                d-flex
                justify-content-between
                align-items-center
                p-1
            ">
                <div className="pl-2">
                    <div>{device.description}</div>
                    <small className="text-secondary">
                        {deviceTypeName(device.type)}
                    </small>
                </div>
                <div
                    className={`
                        flex-shrink-0
                        rounded
                        c-cursor-default
                        c-ft-m
                        c-w-2rem
                        text-center
                        ${uiButtonType}`}
                    onClick={() => dispatch(PMSLocationsActions.toggleProtectDevice(device))}
                >
                    {actionButtonName}
                </div>
            </div>
        );
    }

    function alarmPopup() {
        if (!openPopup) return null;

        const isDevices = openPopup === "devices";

        return (
            <CustomModal
                show={true}
                size="xl"
                centered
                onHide={() => setOpenPopup(null)}
                title={isDevices ? t("devices.devices") : t("global_activity")}
                content={
                    <div style={{ width: "85vw", height: "70vh" }}>
                        {isDevices ? devicesContent() : <PMSLocationProtectLogs />}
                    </div>
                }
            />
        );
    }

    function devicesContent() {
        const { protectDevices, nonProtectDevices } = DeviceHelper.filterProtectDevices(devices);
        return (
            <div className="h-100 d-flex flex-column">
                {incorrectDevices.length > 0 &&
                    <div className="mb-3 flex-shrink-0">
                        <IncorrectDevices incorrectDevices={incorrectDevices} />
                    </div>
                }
                <div className="d-flex no-gutters flex-fill" style={{ minHeight: 0 }}>
                    <div className="col">
                        {selectableDevicesList("REMOVE", protectDevices)}
                    </div>
                    <div className="mr-2" />
                    <div className="col">
                        {selectableDevicesList("ADD", nonProtectDevices)}
                    </div>
                </div>
            </div>
        );
    }

    function infoTooltip(tooltip) {
        return (
            <ToolTipWrapper
                tooltip={
                    <span
                        style={{
                            display: "block",
                            maxWidth: "24rem",
                            textTransform: "none",
                            fontWeight: 400,
                            whiteSpace: "normal",
                        }}
                    >
                        {tooltip}
                    </span>
                }
            >
                <img
                    src={Icons.info}
                    alt="info"
                    className="ml-2"
                    style={{ width: "20px", height: "20px", cursor: "help" }}
                />
            </ToolTipWrapper>
        );
    }
}
